import { Badge, Modal, Tag } from "@appkit4/react-components";
import type { AccordionItemProps } from "@appkit4/react-components/accordion";
import { Accordion, AccordionItem } from "@appkit4/react-components/accordion";
import type { ReactNode } from "react";
import { Children } from "react";
import { FaChevronRight, FaMagic } from "react-icons/fa";

import type { PlanningInsightItemDto } from "../../api/planningInsights";
import { useGetPlanningInsightItems } from "../../api/planningInsights";
import type { RaidInsightItemDto } from "../../api/raid";
import { useGetRaidInsightItems } from "../../api/raid";
import { env } from "../../config/env";
import { formatDisplayDateValue } from "../../utils/dateDisplay";
import { getInsightEvidenceRoute, getInsightEvidenceRouteForLookup } from "./evidenceNavigation";

export type AiInsightLinkedItem = {
  linked_item_id?: string | null;
  linked_item_type?: string | null;
  relationship?: string | null;
  link_basis?: string | null;
  evidence?: string | null;
  confidence?: string | null;
};

type AiInsightDeveloperContext = {
  assessmentScope?: string | null;
  assessmentStatus?: string | null;
  asAt?: string | null;
  materialFlags?: string[] | null;
  missingInputs?: string[] | null;
  limitations?: string[] | null;
  dataQualityAndCaveats?: string[] | null;
  dataUsed?: string[] | null;
};

export function AiInsightSourceMarker() {
  if (!env.showAiInsightsSources) {
    return null;
  }

  return (
    <span
      className="global-insights-overlay__ai-source-marker"
      title="AI-generated insight"
      aria-label="AI-generated insight"
    >
      <FaMagic aria-hidden="true" />
    </span>
  );
}

export function AiInsightDeveloperContext({
  developerContext,
}: {
  developerContext: AiInsightDeveloperContext;
}) {
  const hasDeveloperContext = [
    developerContext.assessmentScope,
    developerContext.assessmentStatus,
    developerContext.asAt,
    ...(developerContext.materialFlags ?? []),
    ...(developerContext.missingInputs ?? []),
    ...(developerContext.limitations ?? []),
    ...(developerContext.dataQualityAndCaveats ?? []),
    ...(developerContext.dataUsed ?? []),
  ].some(Boolean);

  if (!env.enableDevTools || !env.showAiInsightsSources || !hasDeveloperContext) return null;

  return (
    <section className="global-insights-overlay__section global-insights-overlay__ai-dev-context">
      <details>
        <summary className="global-insights-overlay__section-title global-insights-overlay__section-title--raid">
          AI assessment context
        </summary>
        <div className="global-insights-overlay__ai-dev-context-menu">
          {(developerContext.assessmentScope ||
            developerContext.assessmentStatus ||
            developerContext.asAt) && (
            <dl className="global-insights-overlay__ai-dev-context-summary">
              {developerContext.assessmentScope && (
                <>
                  <dt>Scope</dt>
                  <dd>{developerContext.assessmentScope}</dd>
                </>
              )}
              {developerContext.assessmentStatus && (
                <>
                  <dt>Status</dt>
                  <dd>{developerContext.assessmentStatus}</dd>
                </>
              )}
              {developerContext.asAt && (
                <>
                  <dt>As at</dt>
                  <dd>{formatDisplayDateValue(developerContext.asAt)}</dd>
                </>
              )}
            </dl>
          )}
          <DeveloperContextList label="Material flags" items={developerContext.materialFlags} />
          <DeveloperContextList label="Missing inputs" items={developerContext.missingInputs} />
          <DeveloperContextList label="Limitations" items={developerContext.limitations} />
          <DeveloperContextList
            label="Data quality and caveats"
            items={developerContext.dataQualityAndCaveats}
          />
          <DeveloperContextList label="Data used" items={developerContext.dataUsed} />
        </div>
      </details>
    </section>
  );
}

function DeveloperContextList({ label, items }: { label: string; items?: string[] | null }) {
  if (items == null || items.length === 0) return null;
  return (
    <section className="global-insights-overlay__ai-dev-context-section">
      <p>{label}</p>
      <ul>
        {items.map((item, index) => (
          <li key={`${item}-${index}`}>{item}</li>
        ))}
      </ul>
    </section>
  );
}

export function DetailModalShell({
  title,
  onClose,
  children,
  className,
  lastUpdatedAt,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  lastUpdatedAt?: string | null;
}) {
  return (
    <Modal
      visible
      title={title}
      onCancel={onClose}
      closeOnPressEscape
      maskCloseable
      className={[
        "global-insights-overlay__risk-detail-modal",
        "global-insights-overlay__detail-modal-shell",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
      <p className="global-insights-overlay__detail-last-updated">
        Last updated: {lastUpdatedAt != null && lastUpdatedAt.trim() !== ""
          ? formatDisplayDateValue(lastUpdatedAt)
          : "Not available"}
      </p>
    </Modal>
  );
}

export type DetailProvenance =
  | "Source record"
  | "AI assessed"
  | "Inferred"
  | "Rule-based";

export type DetailMetadataItem = {
  label: string;
  value: ReactNode;
  provenance?: DetailProvenance;
};

export function DetailStatusBadge({ value }: { value?: string | null }) {
  if (value == null || value.trim() === "") return null;

  const normalizedValue = value.toLowerCase();
  const tone = /critical|overdue|delayed|delay|blocked|danger|actual delay|late|red/.test(normalizedValue)
    ? "critical"
    : normalizedValue === "high" ||
        /review|concern|risk|stale|partial|at.?risk|escalat|incomplete|unvalid|pending|clash|potential delay|on hold/.test(
          normalizedValue
        )
      ? "warning"
      : /ready|complete|completed|done|validated|resolved|on track|healthy|closed|green/.test(normalizedValue)
        ? "positive"
        : /open|in progress|active|undecided/.test(normalizedValue)
          ? "info"
          : "neutral";

  return (
    <span className="global-insights-overlay__risk-detail-status-badge" data-tone={tone}>
      {value}
    </span>
  );
}

export function DetailMetadataStrip({
  items,
  inline = false,
}: {
  items: DetailMetadataItem[];
  inline?: boolean;
}) {
  return (
    <dl
      className={[
        "global-insights-overlay__risk-detail-metadata",
        inline ? "global-insights-overlay__risk-detail-metadata--inline" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {items.map(({ label, value, provenance }) => (
        <div className="global-insights-overlay__risk-detail-metadata-item" key={label}>
          <dt>{label}</dt>
          <dd>
            {value}
            {provenance && (
              <span className="global-insights-overlay__risk-detail-provenance">{provenance}</span>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function DetailKeyMessage({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section className="global-insights-overlay__risk-detail-key-message" aria-label={label}>
      <p className="global-insights-overlay__risk-detail-key-message-label">{label}</p>
      <div className="global-insights-overlay__risk-detail-text">{children}</div>
    </section>
  );
}

export function DetailAccordionGroup({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  const accordionItems = Children.toArray(children);

  return (
    <Accordion
      className={["global-insights-overlay__risk-detail-accordion-group", className]
        .filter(Boolean)
        .join(" ")}
      multiple
      defaultActivekeys={[]}
    >
      {accordionItems}
    </Accordion>
  );
}

export function DetailAccordionSection({
  heading,
  children,
  aiGenerated = false,
  itemKey,
  badge,
  statusBadge,
  expanded,
  onClickEvent,
  showExpandIcon,
  accordionId,
  accordionRef,
}: {
  heading: string;
  children: ReactNode;
  aiGenerated?: boolean;
  itemKey: string;
  badge?: string | number;
  statusBadge?: ReactNode;
} & Pick<
  AccordionItemProps,
  "expanded" | "onClickEvent" | "showExpandIcon" | "accordionId" | "accordionRef"
>) {
  return (
    <AccordionItem
      className="global-insights-overlay__risk-detail-accordion-item"
      itemKey={itemKey}
      expanded={expanded}
      onClickEvent={onClickEvent}
      showExpandIcon={showExpandIcon}
      accordionId={accordionId}
      accordionRef={accordionRef}
      templateHeader={() => (
        <span className="global-insights-overlay__risk-detail-heading-row">
          <span className="global-insights-overlay__risk-detail-heading">{heading}</span>
          {(statusBadge != null || badge != null || aiGenerated) && (
            <span className="global-insights-overlay__risk-detail-accordion-badges">
              {statusBadge}
              {badge != null && (
                <span className="global-insights-overlay__risk-detail-count">{badge}</span>
              )}
              {aiGenerated && <AiInsightSourceMarker />}
            </span>
          )}
        </span>
      )}
    >
      <div id={itemKey} className="global-insights-overlay__risk-detail-accordion-content">
        {children}
      </div>
    </AccordionItem>
  );
}

export function DetailSubsection({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={["global-insights-overlay__risk-detail-subsection", className]
        .filter(Boolean)
        .join(" ")}
    >
      <p className="global-insights-overlay__risk-detail-sublabel">{label}</p>
      {children}
    </div>
  );
}

export function BulletList({
  items,
  emptyText = "Not available",
}: {
  items: string[];
  emptyText?: string;
}) {
  if (items.length === 0) {
    return <p className="global-insights-overlay__risk-detail-text">{emptyText}</p>;
  }

  return (
    <ul className="global-insights-overlay__risk-detail-list">
      {items.map((item, index) => (
        <li key={`${item}-${index}`}>{item}</li>
      ))}
    </ul>
  );
}

export function ConfidenceBadge({
  value,
  prefix = "",
}: {
  value?: string | null;
  prefix?: string;
}) {
  const normalizedValue = value?.toLowerCase() ?? "";
  const type = normalizedValue.includes("high")
    ? "success-outlined"
    : normalizedValue.includes("medium")
      ? "warning-outlined"
      : normalizedValue.includes("low")
        ? "danger-outlined"
        : "info-outlined";

  const displayValue = prefix ? `${prefix}${value ?? "Not available"}` : (value ?? "Not available");
  return <Badge value={displayValue} type={type} size="sm" />;
}

export function TagList({
  items,
  emptyText = "Not available",
}: {
  items: string[];
  emptyText?: string;
}) {
  if (items.length === 0) {
    return <p className="global-insights-overlay__risk-detail-text">{emptyText}</p>;
  }

  return (
    <div className="global-insights-overlay__risk-detail-chip-list">
      {items.map((item, index) => (
        <Tag key={`${item}-${index}`} label={item} type="outlined" size="sm" closable={false} />
      ))}
    </div>
  );
}

export function EvidenceReference({
  id,
  to,
  sourceType,
  ariaLabel,
}: {
  id: string;
  to?: string;
  sourceType?: string | null;
  ariaLabel?: string;
}) {
  const target = to ?? getInsightEvidenceRoute(id, sourceType);
  if (target == null) {
    return <Tag label={id} type="outlined" size="sm" closable={false} />;
  }

  return (
    <a
      className="global-insights-overlay__risk-detail-evidence-link"
      href={target}
      aria-label={ariaLabel ?? `Go to ${id}`}
      title={ariaLabel ?? `Go to ${id}`}
    >
      {id}
    </a>
  );
}

export function EvidenceReferenceList({
  items,
  emptyText = "Not available",
  getTo,
  getAriaLabel,
  sourceType,
}: {
  items: string[];
  emptyText?: string;
  getTo?: (id: string) => string | undefined;
  getAriaLabel?: (id: string) => string | undefined;
  sourceType?: string | null;
}) {
  const unresolvedIds = items.filter((id) => getInsightEvidenceRoute(id, sourceType) == null);
  const planningLookup = useGetPlanningInsightItems(unresolvedIds);
  const raidLookup = useGetRaidInsightItems(unresolvedIds);

  if (items.length === 0) {
    return <p className="global-insights-overlay__risk-detail-text">{emptyText}</p>;
  }

  const lookupIsLoading = planningLookup.isLoading || raidLookup.isLoading;
  const lookupFailed = planningLookup.isError || raidLookup.isError;

  return (
    <div className="global-insights-overlay__risk-detail-chip-list">
      {items.map((id, index) => {
        const explicitRoute = getTo?.(id) ?? getInsightEvidenceRoute(id, sourceType);
        const matchedPlanningItems = (planningLookup.data ?? []).filter((item) =>
          matchesPlanningEvidenceId(item, id)
        );
        const matchedChecklistItems = matchedPlanningItems.filter((item) => item.isDay1Critical === true);
        const matchedRaidItems = (raidLookup.data ?? []).filter((item) =>
          item.itemId.trim().toLocaleLowerCase() === id.trim().toLocaleLowerCase()
        );
        const matchCount =
          matchedPlanningItems.length + matchedChecklistItems.length + matchedRaidItems.length;
        const resolvedRoute =
          explicitRoute ??
          (matchCount === 1
            ? getInsightEvidenceRouteForLookup(
                id,
                sourceType,
                matchedRaidItems[0]?.raidType,
                matchedPlanningItems.length === 1 && matchedChecklistItems.length === 0,
                matchedChecklistItems.length === 1
              )
            : undefined);

        if (resolvedRoute != null) {
          return (
            <EvidenceReference
              key={`${id}-${index}`}
              id={id}
              to={resolvedRoute}
              ariaLabel={getAriaLabel?.(id)}
              sourceType={sourceType}
            />
          );
        }

        const statusTitle = lookupFailed
          ? "Could not verify this evidence reference"
          : lookupIsLoading
            ? "Checking for a matching source record"
            : matchCount > 1
              ? "This evidence reference matches more than one source record"
              : "No matching source record was found";
        return (
          <span
            key={`${id}-${index}`}
            className="global-insights-overlay__risk-detail-evidence-unresolved"
            title={statusTitle}
            aria-label={`${id}: ${statusTitle}`}
          >
            <Tag label={id} type="outlined" size="sm" closable={false} />
          </span>
        );
      })}
    </div>
  );
}

function matchesPlanningEvidenceId(item: PlanningInsightItemDto, id: string) {
  const normalizedId = id.trim().toLocaleLowerCase();
  return [
    item.planningItemRef,
    item.stableItemId,
    item.sourceActivityId,
    String(item.planningSnapshotItemId),
  ].some((candidate) => candidate?.trim().toLocaleLowerCase() === normalizedId);
}

export function PriorityBadge({ value }: { value?: string | null }) {
  const normalizedValue = value?.toLowerCase();
  const displayValue =
    normalizedValue === "red"
      ? "Critical (Red)"
      : normalizedValue === "amber" || normalizedValue === "yellow"
        ? `Needs attention (${value})`
        : normalizedValue === "green"
          ? "On track (Green)"
          : value;
  const type =
    normalizedValue === "critical" || normalizedValue === "red"
      ? "danger"
      : normalizedValue === "high"
        ? "danger"
        : normalizedValue === "medium" || normalizedValue === "amber" || normalizedValue === "yellow"
        ? "warning"
        : normalizedValue === "low" || normalizedValue === "green"
          ? "success"
          : "primary-outlined";

  return <Badge value={displayValue ?? "Not available"} type={type} size="sm" />;
}

export function StatusBadge({ value }: { value?: string | null }) {
  if (value == null || value.trim() === "") {
    return <Badge value="Not available" type="primary-outlined" size="sm" />;
  }

  const normalizedValue = value.toLowerCase().trim();
  const displayValue =
    normalizedValue === "red"
      ? "Red — At risk"
      : normalizedValue === "amber" || normalizedValue === "yellow"
        ? `${value} — Needs attention`
        : normalizedValue === "green"
          ? "Green — On track"
          : value;
  const type =
    /overdue|delay|delayed|blocked|actual delay|critical|danger|late|red/.test(normalizedValue)
      ? "danger"
      : /hold|risk|amber|yellow|potential delay|concern|review|pending|watch/.test(normalizedValue)
        ? "warning"
        : /closed|complete|completed|done|resolved|decision made|made|green/.test(normalizedValue)
          ? "success"
          : /open|in progress|on track|ready|undecided|active|new/.test(normalizedValue)
            ? "info"
            : "primary-outlined";

  return (
    <Badge
      value={displayValue}
      type={type}
      size="sm"
      style={
        normalizedValue === "open"
          ? {
              backgroundColor: "var(--status-info-bg)",
              borderColor: "var(--status-info-border)",
              color: "var(--status-info-text)",
            }
          : undefined
      }
    />
  );
}

export function DueDateWithCountdown({
  dueDate,
  daysUntilDueDate,
}: {
  dueDate: string;
  daysUntilDueDate: string;
}) {
  const isAvailable = daysUntilDueDate !== "Not available";
  const type = daysUntilDueDate.startsWith("Overdue") ? "danger" : "primary-outlined";

  return (
    <span className="global-insights-overlay__ai-source-value">
      <span style={{ paddingRight: "1rem" }}>{dueDate}</span>
      {isAvailable && <Badge value={daysUntilDueDate} type={type} size="sm" />}
    </span>
  );
}

export type AiInsightsRaidTopItemCardProps = {
  itemId?: string | null;
  title?: string | null;
  priorityBand?: string | null;
  status?: string | null;
  primaryMeta?: string | null;
  secondaryMeta?: string | null;
  owner?: string | null;
  primaryMetaLabel?: string;
  secondaryMetaLabel?: string;
  metaLayout?: "inline" | "aligned";
  aiGenerated?: boolean;
  assumption?: boolean;
  onOpen: () => void;
};

export function AiInsightsRaidTopItemCard({
  itemId,
  title,
  priorityBand,
  status,
  primaryMeta,
  secondaryMeta,
  owner,
  primaryMetaLabel,
  secondaryMetaLabel,
  metaLayout = "inline",
  aiGenerated = false,
  assumption = false,
  onOpen,
}: AiInsightsRaidTopItemCardProps) {
  return (
    <article
      className={[
        "global-insights-overlay__raid-driver-card",
        "global-insights-overlay__raid-driver-card--clickable",
        aiGenerated ? "global-insights-overlay__raid-driver-card--ai-generated" : "",
        assumption ? "global-insights-overlay__raid-driver-card--assumption" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      {aiGenerated && <AiInsightSourceMarker />}
      <div className="global-insights-overlay__raid-driver-copy">
        <div className="global-insights-overlay__raid-driver-header-row">
          {(priorityBand != null || status != null) && (
            <div className="global-insights-overlay__raid-driver-badges">
              {priorityBand != null && <PriorityBadge value={priorityBand} />}
              {status != null && <StatusBadge value={status} />}
            </div>
          )}
          <span className="global-insights-overlay__raid-driver-item-id">{itemId ?? "Not available"}</span>
        </div>
        <h4 className="global-insights-overlay__raid-driver-title global-insights-overlay__raid-driver-title--with-chevron">
          <span>{title ?? "Not available"}</span>
          <FaChevronRight aria-hidden="true" />
        </h4>
        <p
          className={[
            "global-insights-overlay__raid-driver-type",
            metaLayout === "aligned"
              ? "global-insights-overlay__raid-driver-type--aligned"
              : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {primaryMeta?.trim() && (
            <span className="global-insights-overlay__raid-driver-meta-item">
              {primaryMetaLabel ? `${primaryMetaLabel}: ${primaryMeta}` : primaryMeta}
            </span>
          )}
          {secondaryMeta?.trim() && (
            <span
              className={`global-insights-overlay__raid-driver-meta-item global-insights-overlay__raid-driver-workstream${secondaryMetaLabel === "Owner" ? " global-insights-overlay__raid-driver-owner" : ""}`}
            >
              {secondaryMetaLabel ? `${secondaryMetaLabel}: ${secondaryMeta}` : secondaryMeta}
            </span>
          )}
          {owner?.trim() && (
            <span className="global-insights-overlay__raid-driver-meta-item">Owner: {owner}</span>
          )}
        </p>
      </div>
    </article>
  );
}

export function LinkedItems({
  items,
  resolvedItemsById,
}: {
  items: AiInsightLinkedItem[];
  resolvedItemsById?: ReadonlyMap<string, RaidInsightItemDto>;
}) {
  const linkedItemIds = items
    .map((item) => item.linked_item_id?.trim() ?? "")
    .filter(Boolean);
  const planningLookup = useGetPlanningInsightItems(linkedItemIds);
  const raidLookup = useGetRaidInsightItems(linkedItemIds);

  if (items.length === 0) {
    return <p className="global-insights-overlay__risk-detail-text">Not available</p>;
  }

  return (
    <div className="global-insights-overlay__raid-unrecorded-list">
      {items.map((item, index) => {
        const itemId = item.linked_item_id?.trim();
        const normalizedId = itemId?.toLocaleLowerCase();
        const resolvedItem =
          itemId == null
            ? undefined
            : resolvedItemsById?.get(itemId) ??
              [...(resolvedItemsById?.entries() ?? [])].find(
                ([key]) => key.toLocaleLowerCase() === normalizedId
              )?.[1] ??
              raidLookup.data?.find(
                (candidate) => candidate.itemId.trim().toLocaleLowerCase() === normalizedId
              );
        const planningItem = planningLookup.data?.find((candidate) =>
          itemId != null &&
          [
            candidate.planningItemRef,
            candidate.stableItemId,
            candidate.sourceActivityId,
            String(candidate.planningSnapshotItemId),
          ].some((candidateId) => candidateId?.trim().toLocaleLowerCase() === normalizedId)
        );
        const route =
          itemId == null
            ? undefined
            : getInsightEvidenceRouteForLookup(
                itemId,
                item.linked_item_type,
                resolvedItem?.raidType,
                planningItem != null
              );
        const linkedTitle = planningItem?.title ?? resolvedItem?.title ?? "Linked record";
        const content = (
          <>
            <div className="global-insights-overlay__risk-detail-linked-item-header-row">
              <p className="global-insights-overlay__risk-detail-linked-item-id">
                ID: {itemId ?? "Not available"}
              </p>
              <h4 className="global-insights-overlay__risk-detail-linked-item-title">
                {linkedTitle}
              </h4>
              {item.evidence != null && item.evidence.trim() !== "" && (
                <p className="global-insights-overlay__risk-detail-text">{item.evidence}</p>
              )}
            </div>
            <dl className="global-insights-overlay__risk-detail-linked-item-meta">
              <div className="global-insights-overlay__risk-detail-linked-item-meta-pair">
                <dt>Owner:</dt>
                <dd>{planningItem?.owner ?? resolvedItem?.owner ?? "Not available"}</dd>
              </div>
              <div className="global-insights-overlay__risk-detail-linked-item-meta-pair">
                <dt>Status:</dt>
                <dd>{planningItem?.status ?? resolvedItem?.status ?? "Not available"}</dd>
              </div>
              <div className="global-insights-overlay__risk-detail-linked-item-meta-pair">
                <dt>Link type:</dt>
                <dd>{formatMetadataValue(planningItem != null ? "Planning" : resolvedItem?.raidType ?? item.linked_item_type)}</dd>
              </div>
              <div className="global-insights-overlay__risk-detail-linked-item-meta-pair">
                <dt>Relationship:</dt>
                <dd>{formatMetadataValue(item.relationship)}</dd>
              </div>
              <div className="global-insights-overlay__risk-detail-linked-item-meta-pair">
                <dt>Link basis:</dt>
                <dd>{formatMetadataValue(item.link_basis)}</dd>
              </div>
              <div className="global-insights-overlay__risk-detail-linked-item-meta-pair global-insights-overlay__risk-detail-linked-item-confidence">
                <dt>Confidence:</dt>
                <dd><ConfidenceBadge value={item.confidence} /></dd>
              </div>
            </dl>
          </>
        );

        return route != null ? (
          <a
            key={item.linked_item_id ?? `linked-item-${index}`}
            className="global-insights-overlay__risk-detail-linked-item-card global-insights-overlay__risk-detail-linked-item-card--clickable"
            href={route}
            aria-label={`Open linked item ${itemId ?? ""}: ${linkedTitle}`}
          >
            {content}
          </a>
        ) : (
          <article
            key={item.linked_item_id ?? `linked-item-${index}`}
            className="global-insights-overlay__risk-detail-linked-item-card"
          >
            {content}
          </article>
        );
      })}
    </div>
  );
}

function formatMetadataValue(value: string | null | undefined) {
  const normalized = value
    ?.trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ");
  if (!normalized) {
    return "Not available";
  }

  return `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}`;
}
